import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiCheck, FiZap } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import {
  Container,
  Header,
  PlansContainer,
  PlanCard,
  Badge,
  PlanHeader,
  Price,
  FeaturesList,
  SubscribeButton,
  CurrentPlanBadge,
  LoginPrompt,
  LoginButton,
  FAQ,
  FAQItem,
} from './styles'

export function Pricing() {
  const { isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const [loadingPlan, setLoadingPlan] = useState(null)

  const isActiveSubscription = user?.subscriptionStatus === 'active'
  const isFreeUser = !isAuthenticated || user?.subscriptionStatus === 'free'

  const handleSubscribe = async (priceId, planType) => {
    if (!isAuthenticated) {
      toast.info('Faça login para assinar um plano')
      navigate('/login')
      return
    }

    if (isActiveSubscription) {
      toast.info('Você já possui uma assinatura ativa')
      return
    }

    setLoadingPlan(planType)

    try {
      const { data } = await api.post('/stripe/create-checkout-session', {
        priceId: priceId,
      })

      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error('Erro ao criar sessão de pagamento')
      }
    } catch (error) {
      console.error('Error creating checkout session:', error)
      toast.error(error.response?.data?.message || 'Erro ao processar pagamento. Tente novamente.')
    } finally {
      setLoadingPlan(null)
    }
  }

  const handleManageSubscription = async () => {
    try {
      const { data } = await api.post('/stripe/create-portal-session')

      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error('Erro ao abrir portal de gerenciamento')
      }
    } catch (error) {
      console.error('Error creating portal session:', error)
      toast.error('Erro ao abrir portal. Tente novamente.')
    }
  }

  const plans = [
    {
      name: 'Gratuito',
      description: 'Para experimentar',
      price: 0,
      period: 'sempre',
      features: ['3 cálculos por dia', 'Todas as calculadoras'],
      priceId: null,
      type: 'free',
      cta: 'Plano Atual',
    },
    {
      name: 'Mensal',
      description: 'Acesso ilimitado',
      price: 14.9,
      originalPrice: 19.9,
      discount: '25% OFF',
      period: 'mês',
      features: ['Cálculos ilimitados', 'Todas as calculadoras', 'Suporte por email'],
      priceId: process.env.REACT_APP_STRIPE_PRICE_MONTHLY,
      type: 'monthly',
      cta: 'Assinar Mensal',
      featured: false,
    },
    {
      name: 'Anual',
      description: 'Melhor custo-benefício',
      price: 149,
      originalPrice: 199,
      discount: '25% OFF',
      priceMonthly: 12.42,
      period: 'ano',
      savings: 'Economize 17%',
      features: ['Cálculos ilimitados', 'Todas as calculadoras', 'Suporte prioritário via WhatsApp'],
      priceId: process.env.REACT_APP_STRIPE_PRICE_YEARLY,
      type: 'yearly',
      cta: 'Assinar Anual',
      featured: true,
    },
  ]

  if (!isAuthenticated) {
    return (
      <Container>
        <Header>
          <h1>Escolha seu Plano</h1>
          <p>Acesse calculadoras profissionais de engenharia sem limites</p>
        </Header>

        <LoginPrompt>
          <FiZap size={48} color="#6464FF" />
          <h3>Faça login para escolher seu plano</h3>
          <p>Entre na sua conta ou crie uma conta gratuita para começar</p>
          <LoginButton to="/login">Fazer Login</LoginButton>
        </LoginPrompt>

        <FAQ>
          <h2>Perguntas Frequentes</h2>
          <FAQItem>
            <h3>Posso cancelar a qualquer momento?</h3>
            <p>
              Sim! Você pode cancelar sua assinatura a qualquer momento através do portal de gerenciamento. Não há taxas
              de cancelamento.
            </p>
          </FAQItem>
          <FAQItem>
            <h3>Como funciona o período gratuito?</h3>
            <p>
              Com uma conta gratuita, você pode realizar até 3 cálculos por dia. Ideal para conhecer a plataforma antes
              de assinar.
            </p>
          </FAQItem>
          <FAQItem>
            <h3>Quais formas de pagamento são aceitas?</h3>
            <p>Aceitamos cartões de crédito e débito através do Stripe, com processamento seguro e criptografado.</p>
          </FAQItem>
        </FAQ>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <h1>Escolha seu Plano</h1>
        <p>Acesse calculadoras profissionais de engenharia sem limites</p>
      </Header>

      {isActiveSubscription && (
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <SubscribeButton onClick={handleManageSubscription} style={{ maxWidth: '400px', margin: '0 auto' }}>
            Gerenciar Assinatura
          </SubscribeButton>
        </div>
      )}

      <PlansContainer>
        {plans.map((plan) => (
          <PlanCard key={plan.type} $featured={plan.featured}>
            {plan.featured && <Badge>Mais Popular</Badge>}

            <PlanHeader>
              <h2>{plan.name}</h2>
              <p>{plan.description}</p>
            </PlanHeader>

            <Price>
              <div className="amount">
                <span className="currency">R$</span>
                {plan.priceMonthly || plan.price}
              </div>
              <div className="period">por {plan.priceMonthly ? 'mês' : plan.period}</div>
              {plan.originalPrice && (
                <div className="original-price-row">
                  <span className="original-price">R$ {plan.originalPrice}</span>
                  <span className="discount-tag">{plan.discount}</span>
                </div>
              )}
              {plan.savings && <div className="savings">{plan.savings}</div>}
              {plan.priceMonthly && (
                <div className="period" style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                  (R$ {plan.price} cobrado anualmente)
                </div>
              )}
            </Price>

            <FeaturesList>
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <FiCheck size={20} />
                  <span>{feature}</span>
                </li>
              ))}
            </FeaturesList>

            {plan.type === 'free' && isFreeUser ? (
              <CurrentPlanBadge>Plano Atual</CurrentPlanBadge>
            ) : isActiveSubscription && plan.type !== 'free' ? (
              <CurrentPlanBadge>Você já tem acesso ilimitado</CurrentPlanBadge>
            ) : plan.type !== 'free' ? (
              <SubscribeButton
                $primary={plan.featured}
                onClick={() => handleSubscribe(plan.priceId, plan.type)}
                disabled={loadingPlan !== null}
              >
                {loadingPlan === plan.type ? 'Processando...' : plan.cta}
              </SubscribeButton>
            ) : (
              <SubscribeButton disabled>Plano Gratuito</SubscribeButton>
            )}
          </PlanCard>
        ))}
      </PlansContainer>

      <FAQ>
        <h2>Perguntas Frequentes</h2>
        <FAQItem>
          <h3>Posso cancelar a qualquer momento?</h3>
          <p>
            Sim! Você pode cancelar sua assinatura a qualquer momento através do portal de gerenciamento. Não há taxas
            de cancelamento.
          </p>
        </FAQItem>
        <FAQItem>
          <h3>Como funciona o upgrade do plano gratuito?</h3>
          <p>
            Ao assinar um plano pago, você terá acesso ilimitado imediatamente. Seu limite de 3 cálculos por dia será
            removido automaticamente.
          </p>
        </FAQItem>
        <FAQItem>
          <h3>Quais formas de pagamento são aceitas?</h3>
          <p>Aceitamos cartões de crédito e débito através do Stripe, com processamento seguro e criptografado.</p>
        </FAQItem>
        <FAQItem>
          <h3>O que acontece se eu cancelar minha assinatura?</h3>
          <p>
            Você continuará tendo acesso ilimitado até o final do período pago. Após isso, voltará automaticamente para
            o plano gratuito com 3 cálculos por dia.
          </p>
        </FAQItem>
      </FAQ>
    </Container>
  )
}
